import { ChangeEvent, useRef } from 'react'
import { useNavigate } from 'react-router'
import { Button, FormHelperText, Grid, Typography, useTheme } from '@mui/material'
import { Formik, FormikHelpers } from 'formik'
import * as Yup from 'yup'

import FormikTextField from '../../../components/formik-input'
import { useHspPlusContext } from '../../../providers/hsp-plus-provider'
import useIsMobile from '../../../utils/hooks/useIsMobile'
import useSharedStyles from '../../../utils/shared.styles'

import useStyles from './index.styles'

interface OtpFormValues {
  code: string
  digits: string[]
}

const initialValues: OtpFormValues = { code: '', digits: ['', '', '', '', '', ''] }

const validationSchema = Yup.object({
  code: Yup.string().length(6, 'Please try entering your code again').required('Please try entering your code again'),
  digits: Yup.array().of(Yup.string()),
})

const Otp = () => {
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const theme = useTheme()
  const classes = useStyles
  const sharedClasses = useSharedStyles
  const { values: registrationValues } = useHspPlusContext()
  const inputRefs = useRef<Array<HTMLInputElement | null>>([])

  const onPaste = (event: ClipboardEvent | ChangeEvent<HTMLInputElement>, setFieldValue: FormikHelpers<OtpFormValues>['setFieldValue']) => {
    event.preventDefault()
    const pastedCode = (event as ClipboardEvent).clipboardData?.getData('Text') || (event as ChangeEvent<HTMLInputElement>).target.value || ''
    if (pastedCode.length !== 6) return

    const digits = pastedCode.split('')
    digits.forEach((digit, index) => setFieldValue(`digits.${index}`, digit))
    setFieldValue('code', pastedCode)
  }

  const onChange = (
    event: ChangeEvent<HTMLInputElement>,
    index: number,
    values: OtpFormValues,
    setFieldValue: FormikHelpers<OtpFormValues>['setFieldValue']
  ) => {
    let digit = event.target.value

    // Exactly 6 characters, treat the input as a paste (Android Chrome behaves this way); setTimeout is required for Android Chrome issues
    if (digit.length === 6) {
      setTimeout(() => onPaste(event, setFieldValue), 0)
      return
    }

    // More than 1 character, restrict the input to the first character
    if (digit.length > 1) {
      ;[digit] = digit
      setTimeout(() => setFieldValue(`digits.${index}`, digit), 0)
    }

    const nextDigits = [...values.digits]
    nextDigits[index] = digit
    setFieldValue(`digits.${index}`, digit)
    setFieldValue('code', nextDigits.join(''))
    inputRefs.current[index + 1]?.focus()
  }

  return (
    <Formik initialValues={initialValues} onSubmit={() => navigate('/hsp-plus/registration-complete', { replace: true })} validationSchema={validationSchema}>
      {({ values, setFieldValue, errors, submitCount, resetForm, handleSubmit }) => (
        <Grid container={true} component="form" onSubmit={handleSubmit} sx={classes.screen(isMobile)}>
          <Grid container={true} sx={classes.intro(isMobile)}>
            <Typography component="h1" variant="h5" color="textSecondary" sx={classes.title}>
              Last step
            </Typography>
            <Typography variant="body2">
              Confirm your email address. Please enter the code we just sent to {registrationValues.email || 'your email address'}.
            </Typography>
          </Grid>

          <Grid container={true} sx={classes.fields}>
            {values.digits.map((digit, index) => (
              <FormikTextField
                key={index}
                name={`digits.${index}`}
                hideErrors={true}
                className="fs-exclude"
                sx={classes.passcodeInput(theme)}
                autoFocus={index === 0}
                value={digit}
                onPaste={(event) => onPaste(event as unknown as ClipboardEvent, setFieldValue)}
                onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event, index, values, setFieldValue)}
                slotProps={{ htmlInput: { 'aria-label': `code field ${index + 1}`, inputMode: 'numeric' } }}
                inputRef={(element: HTMLInputElement | null) => {
                  inputRefs.current[index] = element
                }}
              />
            ))}
          </Grid>

          <Grid container={true} sx={classes.actions}>
            {submitCount > 0 && errors.code && <FormHelperText sx={sharedClasses.errorText}>{errors.code}</FormHelperText>}
            <Button type="submit" variant="contained" size="large" disableElevation disabled={values.code.length < 6} sx={classes.continueButton}>
              Continue
            </Button>
            <Grid container={true} sx={classes.links}>
              <Button type="button" variant="text" color="primary" onClick={() => resetForm()} sx={sharedClasses.linkButton}>
                Resend code
              </Button>
              <Button type="button" variant="text" color="primary" onClick={() => navigate('/hsp-plus/contact-information')} sx={sharedClasses.linkButton}>
                Change email address
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Formik>
  )
}

export default Otp
